/**
 * Console API shapes (docs/53 §6.4, docs/52 §6, docs/50 §5). Staff only, behind Access. Lead contents and
 * review-request contacts never appear here: those need `operator`, a reason and their own audited route.
 */
import { z } from 'zod';
import { DB_ENUMS, GridPurpose, GridRunStatus, ReportStatus } from '../enums.ts';
import { Day90Inputs, GridPoints, Report, ReportNarrative } from '../api/reports.ts';
import { ReportKey, ReportPeriod } from '../report-period.ts';
import { GridBasis, LatLng, MapsPlace, MoneyService, MoneyServices, SpacingMiles } from '../scans.ts';
import { Cents, Cursor, Day, Email, HttpsUrl, IsoDateTime, JsonValue, Limit, PhoneE164, PositiveCents, Slug, text } from '../common.ts';
import { Focal, PagePhotoSlot, PhotoKind, PhotoKindSource, PhotoNeeds, PhotoProblem, PhotoQuality, StaffPhotoProblem } from '../media.ts';
import { TapCounts } from '../api/reviews.ts';
import { GooglePlaceId } from '../review-link.ts';
import {
  ActorKind,
  GlobalSuppressionReason,
  SuppressionChannel,
  SuppressionReason,
  BillingStatus,
  CreditKind,
  DomainConnectStatus,
  FactSourceKind,
  FactStatus,
  GraduationState,
  HostnameKind,
  HostnameStatus,
  MediaSource,
  MessagingProfileStatus,
  OnboardingStep,
  OnboardingStepState,
  OutputKind,
  OutputState,
  Plan,
  ProposalState,
  RequestStatus,
  RequestType,
  RuleStatus,
  ScreenState,
  Severity,
  SiteVersionStatus,
  StaffRole,
  TenantStatus,
} from '../enums.ts';
import {
  AuditEventId,
  ExportId,
  OpsAlertId,
  CreditId,
  DomainConnectId,
  FactId,
  GbpTaskId,
  GridRunId,
  ScanGridId,
  LeadId,
  LocationId,
  MediaAssetId,
  OutputId,
  PodId,
  ProposalId,
  RequestId,
  ReviewRequestId,
  SiteId,
  StaffId,
  TenantId,
  ViewSessionId,
  WorkflowId,
} from '../ids.ts';

export const StaffMe = z.object({ id: StaffId, email: Email, name: z.string().max(80), role: StaffRole });

export const TodayCard = z.object({
  tenantId: TenantId,
  tenantName: z.string().max(60),
  kind: z.enum(['request', 'content', 'gbp_task', 'onboarding', 'domain', 'messaging', 'billing', 'incident']),
  refId: z.string().max(64),
  title: z.string().max(160),
  severity: Severity.nullable(),
  slaDueAt: IsoDateTime.nullable(),
  assignee: z.object({ id: StaffId, name: z.string().max(80) }).nullable(),
});
export const Today = z.object({ sev1: z.array(TodayCard), dueToday: z.array(TodayCard), dueTomorrow: z.array(TodayCard), waitingOnClient: z.array(TodayCard) });

export const ClientRow = z.object({
  tenantId: TenantId,
  name: z.string().max(60),
  status: TenantStatus,
  stage: OnboardingStep.nullable(),
  health: z.number().int().min(0).max(100).nullable(),
  healthReason: z.string().max(160).nullable(),
  mrrCents: PositiveCents,
  goLiveAt: IsoDateTime.nullable(),
  nextReportOn: Day.nullable(),
  ownerName: z.string().max(80).nullable(),
  lastTouchAt: IsoDateTime.nullable(),
  flags: z.array(z.enum(['texts_pending', 'at_risk', 'write_frozen', 'franchise', 'past_due'])),
  /** What the health number is made of (added by WP-CONSOLE, docs/52 §6 Clients). Optional for older producers. */
  signals: z
    .object({
      healthSource: z.enum(['stored', 'computed']),
      billingStatus: BillingStatus.nullable(),
      onboarding: z.object({
        done: z.number().int().min(0),
        total: z.number().int().min(0),
        clockStartedAt: IsoDateTime.nullable(),
        dueAt: IsoDateTime.nullable(),
        /** Day of the 7-day go-live promise, pauses for "waiting on you" taken out. Null before the clock starts. */
        day: z.number().int().min(0).nullable(),
      }),
      openRequests: z.number().int().min(0),
      nextSlaAt: IsoDateTime.nullable(),
      reviews30d: z.number().int().min(0),
      reviewsPrev30d: z.number().int().min(0),
    })
    .optional(),
});
export const ClientListQuery = z
  .object({ view: z.enum(['all', 'at_risk', 'onboarding', 'texts_pending']).optional(), q: z.string().max(120).optional(), cursor: Cursor.optional(), limit: Limit.optional() })
  .strict();
export const ClientList = z.object({ items: z.array(ClientRow), nextCursor: Cursor.nullable() });

export const Client360 = z.object({
  header: z.object({
    tenantId: TenantId,
    name: z.string().max(60),
    status: TenantStatus,
    plan: Plan.nullable(),
    health: z.number().int().min(0).max(100).nullable(),
    locations: z.array(z.object({ id: LocationId, name: z.string().max(80), podId: PodId.nullable() })),
    writeFreeze: z.boolean(),
  }),
  facts: z.array(z.object({ id: FactId, path: z.string().max(120), value: JsonValue, status: FactStatus, sourceKind: FactSourceKind, sourceRef: z.string().max(200), checkedAt: IsoDateTime })),
  versions: z.array(z.object({ siteId: SiteId, version: z.number().int().min(1), status: SiteVersionStatus, live: z.boolean(), rendererBuild: z.string().max(64), createdAt: IsoDateTime })),
  hostnames: z.array(z.object({ hostname: z.string().max(253), kind: HostnameKind, status: HostnameStatus })),
  onboarding: z.array(
    z.object({
      step: OnboardingStep,
      state: OnboardingStepState,
      dueAt: IsoDateTime.nullable(),
      /** Whether the step holds go-live (facts, domain, brand, licence). Optional: added with the step resolve route. */
      blocksGoLive: z.boolean().optional(),
      doneAt: IsoDateTime.nullable().optional(),
      /** What staff recorded when resolving it, or the problem the Workflow hit (a failed billing start). */
      note: z.string().max(300).nullable().optional(),
    }),
  ),
  requestsOpen: z.number().int().min(0),
  reviews: z.object({ total: z.number().int().min(0), averageRating: z.number().min(0).max(5).nullable(), awaitingReply: z.number().int().min(0) }),
  /** Counts only. Contents need `operator` and a reason (docs/50 50-D13). */
  leads: z.object({ last30Days: z.number().int().min(0), spam30Days: z.number().int().min(0), undelivered: z.number().int().min(0) }),
  billing: z.object({ status: BillingStatus, mrrCents: PositiveCents, currentPeriodEnd: IsoDateTime.nullable(), minTermEnd: IsoDateTime.nullable() }).nullable(),
  messaging: z.array(z.object({ locationId: LocationId, status: MessagingProfileStatus, phoneE164: PhoneE164.nullable(), rejectionReason: z.string().max(300).nullable() })),
  recentAudit: z.array(z.lazy(() => AuditRow)),
  notes: z.array(z.object({ at: IsoDateTime, by: z.string().max(80), text: z.string().max(2000) })),
});

export const ViewSessionReason = z.enum(['request', 'onboarding_help', 'bug_report', 'break_glass']);
export const ViewSessionBody = z
  .object({ reason: ViewSessionReason, requestId: RequestId.optional(), note: text(300).optional() })
  .strict()
  .refine((b) => b.reason !== 'request' || b.requestId !== undefined, { message: 'pick the request', path: ['requestId'] });
export const ViewSession = z.object({ id: ViewSessionId, tenantId: TenantId, reason: ViewSessionReason, startedAt: IsoDateTime, expiresAt: IsoDateTime, readOnly: z.literal(true) });

/** A reasoned read of consumer contact data (docs/53 §4.4). */
export const ContentsAccessBody = z.object({ reason: text(300, 5), requestId: RequestId.optional() }).strict();
export const LeadContents = z.object({ leadId: LeadId, name: z.string().max(120).nullable(), phone: PhoneE164.nullable(), email: Email.nullable(), message: z.string().max(5000).nullable(), auditEventId: AuditEventId });
export const ReviewRequestContacts = z.object({ reviewRequestId: ReviewRequestId, firstName: z.string().max(60).nullable(), phone: PhoneE164.nullable(), email: Email.nullable(), purged: z.boolean(), auditEventId: AuditEventId });

export const QueueRequestRow = z.object({
  id: RequestId,
  tenantId: TenantId,
  tenantName: z.string().max(60),
  number: z.number().int().positive(),
  type: RequestType.nullable(),
  confidence: z.number().min(0).max(1).nullable(),
  status: RequestStatus,
  severity: Severity.nullable(),
  slaDueAt: IsoDateTime.nullable(),
  assignee: StaffId.nullable(),
  subject: z.string().max(160).nullable(),
  createdAt: IsoDateTime,
});
export const RequestQueueQuery = z
  .object({ status: RequestStatus.optional(), type: RequestType.optional(), mine: z.enum(['0', '1']).optional(), cursor: Cursor.optional(), limit: Limit.optional() })
  .strict();
export const RequestQueue = z.object({ items: z.array(QueueRequestRow), nextCursor: Cursor.nullable() });

export const ConsoleProposal = z.object({
  id: ProposalId,
  requestId: RequestId,
  state: ProposalState,
  patch: JsonValue,
  allowlistVersion: z.string().max(40),
  validation: z.array(z.object({ rule: z.string().max(80), ok: z.boolean(), message: z.string().max(300).nullable() })),
  previewVersion: z.number().int().nullable(),
  screenshots: z.array(z.object({ width: z.union([z.literal(390), z.literal(1280)]), before: z.string().max(512), after: z.string().max(512) })),
  textDiff: z.array(z.object({ label: z.string().max(160), before: z.string().max(2000).nullable(), after: z.string().max(2000).nullable() })),
  model: z.string().max(80).nullable(),
  promptVersion: z.string().max(40).nullable(),
  neurons: z.number().int().min(0).nullable(),
});
export const ConsoleRequestDetail = z.object({
  request: QueueRequestRow,
  clientWords: z.string().max(5000),
  messages: z.array(z.object({ at: IsoDateTime, authorKind: z.string().max(20), body: z.string().max(5000), internal: z.boolean() })),
  proposal: ConsoleProposal.nullable(),
});
export const ProposalApproveBody = z.object({ note: text(1000).optional() }).strict();
export const ProposalRejectBody = z.object({ reason: text(1000, 3) }).strict();
export const ProposalEditBody = z.object({ patch: JsonValue, note: text(1000).optional() }).strict();
export const RequestSplitBody = z.object({ parts: z.array(text(2000)).min(2).max(5) }).strict();
export const RequestAssignBody = z.object({ staffId: StaffId.nullable() }).strict();

export const ContentItem = z.object({
  id: OutputId,
  tenantId: TenantId,
  tenantName: z.string().max(60),
  kind: OutputKind,
  state: OutputState,
  draft: JsonValue,
  gates: z.array(z.object({ gate: z.string().max(60), ok: z.boolean(), message: z.string().max(300).nullable() })),
  graduated: z.boolean(),
  createdAt: IsoDateTime,
});
export const ContentQueue = z.object({ items: z.array(ContentItem), nextCursor: Cursor.nullable() });
export const ContentEditBody = z.object({ draft: JsonValue }).strict();

// ---------------------------------------------------------------------------------------------- photos (k11)

/** `review`: processed and waiting for a person (pending or flagged). */
export const ConsoleMediaState = z.enum(['review', 'approved', 'rejected', 'processing', 'all']);
export const ConsoleMediaItem = z.object({
  id: MediaAssetId,
  tenantId: TenantId,
  tenantName: z.string().max(60),
  /** Console paths served after the staff check; never a bucket URL. Null while there's nothing clean to show. */
  thumbPath: z.string().max(512).nullable(),
  fullPath: z.string().max(512).nullable(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  state: ScreenState,
  processing: z.boolean(),
  source: MediaSource,
  kind: PhotoKind.nullable(),
  kindSource: PhotoKindSource.nullable(),
  /** The client's own name for it. */
  title: z.string().max(80).nullable(),
  /** The alt text the site uses once it's placed (docs/20 §8.4): what's visible, never "our" or "we". */
  alt: z.string().max(150).nullable(),
  favourite: z.boolean(),
  excluded: z.boolean(),
  problems: z.array(PhotoProblem).max(6),
  note: z.string().max(160).nullable(),
  quality: PhotoQuality.nullable(),
  focal: Focal,
  duplicateOf: MediaAssetId.nullable(),
  serviceName: z.string().max(80).nullable(),
  /** "hero", "about", "service: Water heaters". */
  placedIn: z.array(z.string().max(80)).max(30),
  createdAt: IsoDateTime,
  reviewedAt: IsoDateTime.nullable(),
});
export const ConsoleMediaQuery = z.object({ tenant: TenantId.optional(), state: ConsoleMediaState.optional(), cursor: Cursor.optional(), limit: Limit.optional() }).strict();
export const ConsoleMediaList = z.object({ items: z.array(ConsoleMediaItem), nextCursor: Cursor.nullable(), waiting: z.number().int().min(0) });

/** Approving says what the photo shows and how the site describes it. Alt text never claims the work ("our", "we"). */
export const MediaApproveBody = z
  .object({ kind: PhotoKind, alt: text(150, 5) })
  .strict()
  .refine((b) => !/\b(our|we|us)\b/i.test(b.alt), { message: 'describe what is visible; never "our" or "we"', path: ['alt'] });
export const MediaRejectBody = z.object({ reason: StaffPhotoProblem, note: text(160).optional() }).strict();
export const MediaFocalBody = Focal.strict();
/** Put a photo in a page slot or on a service, or take it out (`on: false`). */
export const MediaAssignBody = z
  .object({ slot: z.union([PagePhotoSlot, z.literal('service')]), service: Slug.optional(), on: z.boolean() })
  .strict()
  .refine((b) => (b.slot === 'service') === (b.service !== undefined), { message: 'name the service', path: ['service'] });

/** One client's photos at a glance: the go-live rule and what fills each slot today. */
export const ClientPhotos = z.object({
  tenantId: TenantId,
  tenantName: z.string().max(60),
  live: z.boolean(),
  needs: PhotoNeeds,
  slots: z.array(z.object({ slot: PagePhotoSlot, assetId: MediaAssetId.nullable(), stock: z.boolean() })),
  services: z.array(z.object({ slug: Slug, name: z.string().max(80), assetId: MediaAssetId.nullable(), stock: z.boolean() })),
});
export const BulkApproveBody = z.object({ ids: z.array(OutputId).min(1).max(100) }).strict();

export const GbpTask = z.object({
  id: GbpTaskId,
  tenantId: TenantId,
  locationId: LocationId,
  podId: PodId.nullable(),
  instruction: z.string().max(2000),
  before: JsonValue,
  subject: z.object({ type: z.enum(['output', 'proposal', 'decision']), id: z.string().max(64) }),
  state: z.enum(['open', 'done', 'problem']),
  createdAt: IsoDateTime,
});
export const GbpTaskList = z.object({ items: z.array(GbpTask), nextCursor: Cursor.nullable() });
export const GbpTaskDoneBody = z.object({ evidenceUrl: HttpsUrl.optional(), evidenceUploadId: z.string().max(64).optional(), note: text(1000).optional() }).strict();
export const GbpTaskProblemBody = z.object({ problem: text(1000, 3) }).strict();

export const OnboardingBoardRow = z.object({
  tenantId: TenantId,
  name: z.string().max(60),
  step: OnboardingStep,
  state: OnboardingStepState,
  clockStartedAt: IsoDateTime.nullable(),
  deadlineAt: IsoDateTime.nullable(),
  waitingOnClientDays: z.number().int().min(0),
  stuck: z.boolean(),
  /** A problem the go-live Workflow recorded (billing couldn't start, a failed publish). Optional. */
  problem: z.string().max(300).nullable().optional(),
});
export const OnboardingBoard = z.object({ items: z.array(OnboardingBoardRow) });
export const OnboardingExtendBody = z.object({ days: z.number().int().min(1).max(30), reason: text(300, 3) }).strict();
export const OnboardingNudgeBody = z.object({ step: OnboardingStep, channel: z.enum(['email', 'push', 'sms']) }).strict();

export const DomainRow = z.object({
  id: DomainConnectId,
  tenantId: TenantId,
  domain: z.string().max(253),
  path: z.string().max(40),
  status: DomainConnectStatus,
  records: z.array(z.object({ type: z.string().max(8), name: z.string().max(253), ok: z.boolean() })),
  certificate: z.enum(['pending', 'active', 'failed', 'unknown']),
  startedAt: IsoDateTime,
  daysSinceStart: z.number().int().min(0),
});
export const DomainList = z.object({ items: z.array(DomainRow) });

export const MessagingRow = z.object({
  tenantId: TenantId,
  locationId: LocationId,
  status: MessagingProfileStatus,
  phoneE164: PhoneE164.nullable(),
  rejectionReason: z.string().max(300).nullable(),
  sends7d: z.number().int().min(0),
  optOuts7d: z.number().int().min(0),
  carrierErrors7d: z.number().int().min(0),
  emailOnly: z.boolean(),
});
export const MessagingList = z.object({ items: z.array(MessagingRow) });
export const MessagingResubmitBody = z.object({ fields: z.record(z.string(), JsonValue), note: text(500).optional() }).strict();
export const MessagingEmailOnlyBody = z.object({ emailOnly: z.boolean(), reason: text(300, 3) }).strict();

// ---------------------------------------------------------------------------------------------- review sends (c072, c074, c133)

/**
 * Review email sending per client (docs/52 §6 "Messaging"): what a send needs (the Google place ID and a postal
 * address), the last 7 days of asks, sends, failures, opt-outs and link taps, and whether staff paused the client's
 * asks. No contact ever appears here: suppressions are hashes, so a row shows a short reference, never an address.
 */
export const ReviewSendsRow = z.object({
  tenantId: TenantId,
  tenantName: z.string().max(60),
  tenantStatus: TenantStatus,
  locationId: LocationId,
  locationName: z.string().max(80),
  placeId: z.string().max(300).nullable(),
  hasAddress: z.boolean(),
  linkUrl: HttpsUrl.nullable(),
  paused: z.boolean(),
  pausedReason: z.string().max(300).nullable(),
  asks7d: z.number().int().min(0),
  sent7d: z.number().int().min(0),
  failed7d: z.number().int().min(0),
  /** Sends the mail service refused or bounced in the last 7 days. */
  undeliverable7d: z.number().int().min(0),
  optOuts7d: z.number().int().min(0),
  /** Every address on this business's suppression list, for good. */
  suppressed: z.number().int().min(0),
  taps7d: TapCounts,
});
export const ReviewSendsList = z.object({ items: z.array(ReviewSendsRow), globalSuppressed: z.number().int().min(0) });
export const ReviewSendsPauseBody = z.object({ paused: z.boolean(), reason: text(300, 3) }).strict();

export const ReviewPlaceBody = z.object({ placeId: GooglePlaceId }).strict();

export const SuppressionRow = z.object({
  /** The first characters of the salted hash: enough to tell rows apart, useless for finding the address. */
  ref: z.string().max(16),
  channel: SuppressionChannel,
  reason: SuppressionReason,
  createdAt: IsoDateTime,
});
export const SuppressionList = z.object({ items: z.array(SuppressionRow), total: z.number().int().min(0) });
export const SuppressionEmailBody = z.object({ email: Email }).strict();
export const SuppressionAddBody = z.object({ email: Email, note: text(300, 3) }).strict();
/** Whether one address is suppressed for a business, and for every business (hard bounces and complaints). */
export const SuppressionCheck = z.object({
  business: z.object({ suppressed: z.boolean(), reason: SuppressionReason.nullable(), since: IsoDateTime.nullable() }),
  everywhere: z.object({ suppressed: z.boolean(), reason: GlobalSuppressionReason.nullable(), since: IsoDateTime.nullable(), until: IsoDateTime.nullable() }),
});
export const GlobalSuppressionRow = z.object({
  ref: z.string().max(16),
  reason: GlobalSuppressionReason,
  source: z.string().max(60),
  createdAt: IsoDateTime,
  /** Null means for good. The mail service lifts some bounces after a week. */
  expiresAt: IsoDateTime.nullable(),
});
export const GlobalSuppressionList = z.object({ items: z.array(GlobalSuppressionRow), total: z.number().int().min(0) });

export const PublishingRow = z.object({
  tenantId: TenantId,
  siteId: SiteId,
  liveVersion: z.number().int().nullable(),
  pendingVersions: z.array(z.number().int()),
  rendererPin: z.string().max(64).nullable(),
  lastPublishAt: IsoDateTime.nullable(),
  lastResult: z.enum(['ok', 'failed', 'rolled_back']).nullable(),
});
export const PublishingList = z.object({ items: z.array(PublishingRow) });
export const RollbackBody = z.object({ toVersion: z.number().int().min(1), reason: text(300, 3) }).strict();
export const PinBody = z.object({ rendererBuild: z.string().max(64).nullable(), until: IsoDateTime.optional(), reason: text(300, 3) }).strict();

export const BillingExceptionRow = z.object({
  tenantId: TenantId,
  name: z.string().max(60),
  kind: z.enum(['payment_failed', 'credit_due', 'canceling', 'refund_pending']),
  status: BillingStatus,
  amountCents: Cents.nullable(),
  since: IsoDateTime,
  effectiveAt: IsoDateTime.nullable(),
});
export const BillingExceptions = z.object({ items: z.array(BillingExceptionRow) });
export const CreditBody = z.object({ kind: CreditKind, amountCents: z.number().int().max(-1).min(-1_000_000), reason: text(300, 3) }).strict();
export const CreditView = z.object({ id: CreditId, kind: CreditKind, amountCents: Cents, reason: z.string().max(300), approvedBy: StaffId.nullable(), createdAt: IsoDateTime });

export const Health = z.object({
  heartbeats: z.array(z.object({ job: z.string().max(60), lastOkAt: IsoDateTime.nullable(), lastError: z.string().max(500).nullable(), late: z.boolean() })),
  workflowsFailed: z.array(z.object({ id: WorkflowId, name: z.string().max(60), tenantId: TenantId.nullable(), error: z.string().max(500), at: IsoDateTime })),
  queues: z.array(z.object({ queue: z.string().max(40), depth: z.number().int().min(0), oldestAgeSeconds: z.number().int().min(0) })),
  ai: z.object({ neuronsToday: z.number().int().min(0), dailyFree: z.literal(10_000), capToday: z.number().int().min(0) }),
  graduation: z.array(z.object({ draftType: z.string().max(60), promptVersion: z.string().max(40), state: GraduationState, cleanRun: z.number().int().min(0), edits: z.number().int().min(0) })),
});
/** Needs attention (Stage 1): dead letters, missed heartbeats, failed synthetic leads, held go-lives. */
export const AlertSeverity = z.enum(DB_ENUMS['ops_alerts.severity']);
export const OpsAlert = z.object({
  id: OpsAlertId,
  tenantId: TenantId.nullable(),
  tenantName: z.string().max(60).nullable(),
  severity: AlertSeverity,
  source: z.string().max(40),
  kind: z.string().max(60),
  title: z.string().max(200),
  detail: z.string().max(1000).nullable(),
  count: z.number().int().min(1),
  firstAt: IsoDateTime,
  lastAt: IsoDateTime,
  emailedAt: IsoDateTime.nullable(),
  resolvedAt: IsoDateTime.nullable(),
  resolvedBy: z.string().max(80).nullable(),
  note: z.string().max(500).nullable(),
});
export const OpsAlertList = z.object({ items: z.array(OpsAlert), open: z.number().int().min(0) });
export const OpsAlertQuery = z.object({ state: z.enum(['open', 'resolved']).optional() }).strict();
export const OpsAlertResolveBody = z.object({ note: text(500, 3) }).strict();
export const SilenceBody = z.object({ key: z.string().max(120), until: IsoDateTime, reason: text(300, 3) }).strict();

export const AuditRow = z.object({
  seq: z.number().int().positive(),
  id: AuditEventId,
  at: IsoDateTime,
  tenantId: TenantId.nullable(),
  actorKind: ActorKind,
  actorId: z.string().max(64).nullable(),
  via: z.string().max(20),
  action: z.string().max(80),
  targetType: z.string().max(40).nullable(),
  targetId: z.string().max(64).nullable(),
  after: JsonValue.nullable(),
  viewSessionId: ViewSessionId.nullable(),
});
export const AuditQuery = z
  .object({
    tenant: TenantId.optional(),
    actor: z.string().max(64).optional(),
    action: z.string().max(80).optional(),
    from: Day.optional(),
    to: Day.optional(),
    cursor: Cursor.optional(),
    limit: Limit.optional(),
  })
  .strict();
export const AuditPage = z.object({ items: z.array(AuditRow), nextCursor: Cursor.nullable(), chainOk: z.boolean().nullable() });

export const EngineSummary = z.object({
  cohorts: z.array(z.object({ id: z.string().max(64), sent: z.number().int().min(0), replies: z.number().int().min(0), pageViews: z.number().int().min(0), checkoutsStarted: z.number().int().min(0), checkoutsCompleted: z.number().int().min(0) })),
  asOf: IsoDateTime,
});

export const RuleRow = z.object({ state: z.string().length(2), trade: z.string().max(20), version: z.number().int().min(1), status: RuleStatus, reviewDue: Day.nullable(), verifiedBy: z.string().max(80).nullable() });
export const RuleList = z.object({ items: z.array(RuleRow) });

export const FreezeBody = z
  .object({ scope: z.enum(['global', 'pod', 'tenant']), target: z.string().max(64).optional(), on: z.boolean(), reason: text(300, 3) })
  .strict()
  .refine((b) => b.scope === 'global' || b.target !== undefined, { message: 'pick what to freeze', path: ['target'] });
export const Incidents = z.object({
  globalFreeze: z.boolean(),
  podFreezes: z.array(z.object({ podId: PodId, on: z.boolean(), since: IsoDateTime })),
  tenantFreezes: z.array(z.object({ tenantId: TenantId, since: IsoDateTime })),
  statusPageUrl: HttpsUrl.nullable(),
});

// ------------------------------------------------------------------------------------------------ added by WP-CONSOLE

/**
 * What the client sees, as staff see it in "view as client" (docs/50 §5.3): read-only, time-boxed and shown to the
 * client in their activity feed. Lead rows carry no contact details (contents need `operator` and a reason).
 */
export const ViewAsSnapshot = z.object({
  session: z.object({ id: ViewSessionId, tenantId: TenantId, reason: ViewSessionReason, startedAt: IsoDateTime, expiresAt: IsoDateTime, readOnly: z.literal(true), staffName: z.string().max(80) }),
  business: z.object({
    name: z.string().max(60),
    status: TenantStatus,
    approvalMode: z.enum(['we_handle_it', 'show_me_first']),
    liveHost: z.string().max(253).nullable(),
    liveVersion: z.number().int().nullable(),
  }),
  onboarding: z.array(z.object({ step: OnboardingStep, state: OnboardingStepState })),
  needsYou: z.array(z.object({ kind: z.enum(['approval', 'decision', 'review']), title: z.string().max(160), at: IsoDateTime })),
  requests: z.array(z.object({ number: z.number().int().positive(), subject: z.string().max(160).nullable(), status: RequestStatus, createdAt: IsoDateTime })),
  leads: z.array(z.object({ id: LeadId, source: z.string().max(30), status: z.string().max(20), createdAt: IsoDateTime })),
  reviews: z.array(z.object({ rating: z.number().int().min(1).max(5), author: z.string().max(80).nullable(), excerpt: z.string().max(200).nullable(), replyState: z.string().max(20), at: IsoDateTime })),
  whatWeDid: z.array(z.object({ kind: OutputKind, url: z.string().max(512).nullable(), at: IsoDateTime })),
  billing: z.object({ status: BillingStatus, currentPeriodEnd: IsoDateTime.nullable() }).nullable(),
});

/** The share of a batch a reviewer must open before bulk approving (always at least one item). */
export const BULK_SAMPLE_RATE = 0.2;
export const bulkSampleSize = (n: number): number => Math.max(1, Math.ceil(n * BULK_SAMPLE_RATE));

/** Bulk approve with sampling: the reviewer opens a share of the batch first and names what they checked. */
export const SampledBulkBody = z
  .object({ ids: z.array(ProposalId).min(1).max(100), sampledIds: z.array(ProposalId).min(1).max(100), note: text(1000).optional() })
  .strict()
  .refine((b) => b.sampledIds.every((s) => b.ids.includes(s)), { message: 'sampled items must be in the batch', path: ['sampledIds'] })
  .refine((b) => new Set(b.sampledIds).size >= bulkSampleSize(new Set(b.ids).size), { message: 'open more of the batch first', path: ['sampledIds'] });
export const BulkResult = z.object({ approved: z.array(ProposalId), skipped: z.array(z.object({ id: ProposalId, why: z.string().max(160) })) });

/** A staff reply on a request: to the client, or an internal note only staff see. */
export const RequestReplyBody = z.object({ body: text(5000), internal: z.boolean() }).strict();
export const ConsoleRequestMessage = z.object({ at: IsoDateTime, authorKind: z.string().max(20), body: z.string().max(5000), internal: z.boolean() });

export const ClientNoteBody = z.object({ body: text(2000) }).strict();

/**
 * A client export started by staff (deck ruling c013: the site's files leave only through staff). `everything`
 * from staff carries the site's content and files as well as the client's data; `site_static` is the site's files
 * only; `audit` the audit log. The reason is audited. The zip is downloaded through the console, never emailed.
 */
export const ConsoleExportBody = z.object({ kind: z.enum(DB_ENUMS['exports.kind']), reason: text(300, 3) }).strict();
export const ConsoleExportRow = z.object({
  id: ExportId,
  kind: z.enum(DB_ENUMS['exports.kind']),
  status: z.enum(DB_ENUMS['exports.status']),
  bytes: z.number().int().min(0).nullable(),
  createdAt: IsoDateTime,
  expiresAt: IsoDateTime.nullable(),
  /** The console path that streams the zip while it's ready and not expired. */
  downloadPath: z.string().max(300).nullable(),
});
export const ConsoleExportList = z.object({ items: z.array(ConsoleExportRow).max(50) });

/** The prospect pipeline (docs/10, docs/12): prospect tenants in `core` and their page record in KV. */
export const ProspectStage = z.enum(['preview_building', 'preview_ready', 'page_live', 'page_expired', 'page_withdrawn', 'converted']);
export const ProspectRow = z.object({
  tenantId: TenantId,
  name: z.string().max(60),
  slug: z.string().max(60),
  state: z.string().length(2).nullable(),
  status: TenantStatus,
  stage: ProspectStage,
  pageSlug: z.string().max(40).nullable(),
  pageUrl: z.string().max(2048).nullable(),
  previewVersion: z.number().int().nullable(),
  expiresAt: IsoDateTime.nullable(),
  eligible: z.boolean().nullable(),
  createdAt: IsoDateTime,
});
export const ProspectList = z.object({ items: z.array(ProspectRow), engine: EngineSummary.nullable() });
export const ProspectPublishBody = z.object({ days: z.number().int().min(1).max(31), eligible: z.boolean(), keyword: z.string().trim().max(120).optional() }).strict();
export const ProspectWithdrawBody = z.object({ reason: text(300, 3) }).strict();
export const ProspectConvertBody = z.object({ ownerEmail: Email, plan: Plan, reason: text(300, 5) }).strict();
export const ProspectConverted = z.object({ tenantId: TenantId, inviteSentTo: Email, expiresAt: IsoDateTime });

/** Staff users (Settings). Changing them is owner only, with a fresh Access login. */
export const StaffRow = z.object({ id: StaffId, email: Email, name: z.string().max(80), role: StaffRole, active: z.boolean(), createdAt: IsoDateTime });
export const StaffList = z.object({ items: z.array(StaffRow) });
export const StaffCreateBody = z.object({ email: Email, name: text(80, 2), role: StaffRole }).strict();
export const StaffUpdateBody = z
  .object({ role: StaffRole.optional(), active: z.boolean().optional() })
  .strict()
  .refine((b) => b.role !== undefined || b.active !== undefined, { message: 'nothing to change' });

// ---------------------------------------------------------------------------------------------- scans, maps and reports
// docs/55 §3.1 step 9 (grids, monthly reports and the day 90 read). `jobs` owns the logic and answers these through
// JobsRpc; the console routes check the staff role, pass the call on and never compute a number themselves.

/** A client's grid: 81 points, spaced from their real service area at baseline, recorded and never changed (c011). */
export const ScanGridView = z.object({
  id: ScanGridId,
  locationId: LocationId,
  center: LatLng,
  size: z.literal(9),
  spacingMiles: z.number().positive().max(10),
  serviceRadiusMiles: z.number().positive().max(200),
  basis: GridBasis,
  note: z.string().max(300),
  createdAt: IsoDateTime,
  /** Set when the baseline starts. After that nothing about the grid can change. */
  lockedAt: IsoDateTime.nullable(),
});

/** What the grid would be if it were set now, and what's missing. */
export const GridProposal = z.object({
  locationId: LocationId.nullable(),
  /** The shop's pin. Null when the location has none: a person has to give it. */
  center: LatLng.nullable(),
  spacingMiles: z.number().positive().max(10),
  serviceRadiusMiles: z.number().positive().max(200),
  basis: GridBasis,
  note: z.string().max(300),
  problems: z.array(z.string().max(200)).max(10),
});

export const MoneyServicesView = z.object({
  services: z.array(MoneyService).max(3),
  /** Agreed in writing: the client confirmed the fact, or staff recorded their written yes (c010). */
  agreed: z.boolean(),
  agreedAt: IsoDateTime.nullable(),
});

export const ScanRunRow = z.object({
  id: GridRunId,
  serviceSlug: z.string().max(64),
  keyword: z.string().max(120),
  purpose: GridPurpose,
  cycle: z.string().max(20),
  slot: z.string().max(12),
  status: GridRunStatus,
  runAt: IsoDateTime,
  top3: z.number().int().min(0).max(81).nullable(),
  top3Share: z.number().min(0).max(1).nullable(),
  pointsOk: z.number().int().min(0),
  pointsFailed: z.number().int().min(0),
  pointsTotal: z.number().int().min(0),
  costMicros: z.number().int().min(0),
  provider: z.string().max(40).nullable(),
  error: z.string().max(300).nullable(),
  hasPoints: z.boolean(),
});

const Spend = z.object({ spentMicros: z.number().int().min(0), capMicros: z.number().int().min(0), points: z.number().int().min(0) });
/** Today's scan spend against the caps (S13: $2 a day across everything, enforced in code). */
export const ScanBudgetView = z.object({ day: Day, pointCostMicros: z.number().int().min(0), global: Spend, tenant: Spend.nullable(), prospects: Spend });

/** Which map provider is wired. `off` means no scan can run: the secrets aren't set. */
export const ScanProviderView = z.object({ name: z.enum(['dataforseo', 'fixture', 'off']), ready: z.boolean(), reason: z.string().max(200).nullable() });

export const ClientScans = z.object({
  tenantId: TenantId,
  grid: ScanGridView.nullable(),
  proposal: GridProposal.nullable(),
  moneyServices: MoneyServicesView,
  /** The client's active services, to pick the three from. */
  services: z.array(z.object({ slug: z.string().max(64), name: z.string().max(80) })).max(100),
  runs: z.array(ScanRunRow).max(300),
  schedule: z.object({
    goLiveDay: Day.nullable(),
    baselineDone: z.boolean(),
    day90Done: z.boolean(),
    nextDay: Day.nullable(),
    nextPurpose: z.enum(['baseline', 'monthly', 'day90']).nullable(),
    /** Why nothing is scanning right now, in plain words. Null when a scan is due and can start. */
    waiting: z.string().max(200).nullable(),
  }),
  day90: Day90Inputs.extend({ result: z.enum(DB_ENUMS['guarantee_reads.result']), computedAt: IsoDateTime }).nullable(),
  budget: ScanBudgetView,
  provider: ScanProviderView,
});

export const GridSetBody = z
  .object({ locationId: LocationId.optional(), center: LatLng, spacingMiles: SpacingMiles.optional(), reason: text(300, 3).optional() })
  .strict()
  .refine((b) => b.spacingMiles === undefined || !!b.reason, { message: 'say why the spacing differs from the service area', path: ['reason'] });
export const MoneyServicesSetBody = z.object({ services: MoneyServices, agreed: z.boolean(), note: text(300).optional() }).strict();

export const ScanStartBody = z.discriminatedUnion('kind', [
  /** Start whatever the schedule says is due now (the baseline, a four-weekly scan or the day 90 pair). */
  z.object({ kind: z.literal('due') }).strict(),
  /** Fetch only the points a run is missing. */
  z.object({ kind: z.literal('resume'), gridRunId: GridRunId }).strict(),
  /** A check scan after a sensitive profile change, on the client's own grid. */
  z.object({ kind: z.literal('regrid'), reason: text(300, 3) }).strict(),
  /** One keyword for a prospect, on the fixed prospect grid, under the prospect budget. */
  z.object({ kind: z.literal('prospect'), keyword: text(80, 3), center: LatLng }).strict(),
]);
export const ScanStartResult = z.object({
  started: z.array(z.object({ workflowId: WorkflowId, gridRunId: GridRunId })).max(12),
  /** What happened in plain words ("Baseline started: 6 scans." or why nothing started). */
  message: z.string().max(300),
});

/** Who shows up on one scan, most top-three points first. Staff see names; `ours` marks a current client (D-a). */
export const ScanRoster = z.object({
  gridRunId: GridRunId,
  keyword: z.string().max(120),
  places: z
    .array(MapsPlace.omit({ rank: true }).extend({ top3Points: z.number().int().min(0).max(81), found: z.number().int().min(0).max(81), bestRank: z.number().int().min(1).max(100), ours: z.boolean(), self: z.boolean() }))
    .max(60),
});

/** A prospect's map as a prospect may see it: D-a applied, so a current client is "Another local company". */
export const ProspectMap = z.object({ gridRunId: GridRunId, points: GridPoints, top3Points: z.number().int().min(0).max(81), roster: ScanRoster.shape.places });

export const ConsoleReportRow = z.object({
  key: ReportKey,
  status: ReportStatus,
  period: ReportPeriod.nullable(),
  headline: z.string().max(200).nullable(),
  scanDay: Day.nullable(),
  /** The day it publishes itself unless held: scan day plus 5 (r26). */
  publishDueOn: Day.nullable(),
  held: z.boolean(),
  holdReason: z.string().max(300).nullable(),
  /** Why a person should read it before it goes out (a first three months report, a map that moved down). */
  mustRead: z.string().max(200).nullable(),
  publishedAt: IsoDateTime.nullable(),
  publishedBy: z.string().max(80).nullable(),
  editedAt: IsoDateTime.nullable(),
});
export const ConsoleReportList = z.object({ items: z.array(ConsoleReportRow).max(100) });
export const ConsoleReport = ConsoleReportRow.extend({ report: Report });

export const ReportActionBody = z.discriminatedUnion('action', [
  z.object({ action: z.literal('edit'), headline: text(200, 3).optional(), narrative: ReportNarrative.optional() }).strict(),
  z.object({ action: z.literal('hold'), reason: text(300, 3) }).strict(),
  z.object({ action: z.literal('release') }).strict(),
  z.object({ action: z.literal('publish') }).strict(),
  /** Count the numbers again from the data (a late Google figure, a lead marked spam) without touching the words. */
  z.object({ action: z.literal('refresh') }).strict(),
]);
export type ReportActionBody = z.infer<typeof ReportActionBody>;
